import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import lodashCloneDeep from "lodash/cloneDeep";
import lodashKebabCase from "lodash/kebabCase";
import lodashUpperFirst from "lodash/upperFirst";

import { getTodayStartEndDate } from "@/common/date.utilities";
import { SOURCE_METADATA } from "@/common/constants/search";
import { ACCEPTED_PARAMS } from "@/common/constants/apiUtilities/acceptedParams";
import { isFeatureUnlocked } from "@/common/featureLock";
import { transformRadiusToKm } from "@/common/basic.utilities";
import { getAssets } from "@/services/assetManagement/assets.service";

export {
  formatSavedSearchParams,
  unformatSavedSearchParams,
  setSourceMetadataDynamicConstants,
};

const formatSavedSearchParams = async (wodParams = {}, uiParams = {}) => {
  const {
    bounds,
    topics,
    sources,
    riskCategories,
    tags,
    aoiId,
    hotspotsOnly,
    veAssets,
    pois,
    poisDistUnits,
    radius,
    end,
    ...otherParams
  } = lodashCloneDeep(wodParams);

  return {
    ..._filterDynamicParams(otherParams),
    end: end ?? getTodayStartEndDate(dayjs.utc()).end,
    ..._formatSourceMetadata(otherParams),
    ...(pois &&
      uiParams.selectedPlaces?.length &&
      _formatPlaces(
        uiParams.placesData,
        uiParams.selectedPlaces,
        radius,
        poisDistUnits
      )),
    ...(pois &&
      uiParams.selectedAssets?.length &&
      (await _formatAssets(uiParams.selectedAssets, radius, poisDistUnits))),
    ...(aoiId && { aoiId: aoiId.split(",") }),
    ...(bounds && wodParams.boundsType === "mapextent" && { bounds: bounds }),
    ...(topics && { topics: topics.split(",") }),
    ...(sources && { sources: sources.split(",") }),
    ...(riskCategories && { riskCategories: riskCategories.split(",") }),
    ...(tags && { tags: tags.split(",") }),
    ...(hotspotsOnly && { hotspotsOnly: true }),
    ...(veAssets && { assets: veAssets }),
    ...(uiParams.time_range && { time_range: uiParams.time_range }),
  };
};

const unformatSavedSearchParams = (paramsIn = {}) => {
  let {
    assets,
    places,
    tags,
    topics,
    sources,
    riskCategories,
    placesDistUnits,
    ...params
  } = paramsIn;

  return {
    ...params,
    ...(placesDistUnits && { poisDistUnits: placesDistUnits }),
    ...(assets?.length && { veAssets: lodashCloneDeep(assets) }),
    ...(places?.length &&
      params.boundsType === "assets" &&
      _unformatAssets(places)),
    ...(places?.length &&
      params.boundsType === "places" && { places: lodashCloneDeep(places) }),
    ...(tags && { tags: tags.join(",") }),
    ...(topics && { topics: topics.join(",") }),
    ...(sources && { sources: sources.join(",") }),
    ...(riskCategories && { riskCategories: riskCategories.join(",") }),
  };
};

function _filterDynamicParams(params = {}) {
  const paramsCopy = lodashCloneDeep(params);
  const paramsToDelete = SOURCE_METADATA.map(
    (type) => `sourceMetadata${lodashUpperFirst(type)}`
  );
  paramsToDelete.forEach((p) => delete paramsCopy[p]);
  return paramsCopy;
}

function _formatSourceMetadata(params) {
  const sourceMetadata = SOURCE_METADATA.reduce((sm, type) => {
    sm[type] =
      params[`sourceMetadata${lodashUpperFirst(type)}`]?.split(",") ?? [];
    return sm;
  }, {});
  if (Object.entries(sourceMetadata).some(([k, v]) => v.length)) {
    return {
      source_metadata: sourceMetadata,
    };
  }
}

function _formatPlaces(placesData, selectedPlaceIds, radius, poisDistUnits) {
  const placesDataSimplified = placesData.map((p) => p.data?.features).flat();
  const formattedRadius = transformRadiusToKm(radius, poisDistUnits);

  return {
    places: selectedPlaceIds
      .map((id) => {
        const matchingPlace = placesDataSimplified.find(
          (place) => place?.id === id && place?.geometry?.coordinates
        );
        if (matchingPlace) {
          // places: [[lat,lon,radius,place_id,PoisDistUnits]]
          return [
            matchingPlace.geometry.coordinates[0],
            matchingPlace.geometry.coordinates[1],
            formattedRadius,
            matchingPlace.id,
            "km",
          ];
        }
      })
      .filter((p) => !!p),
    placesDistUnits: "km",
    boundsType: "places",
  };
}

async function _formatAssets(assetIds, radius, poisDistUnits) {
  const formattedRadius = transformRadiusToKm(radius, poisDistUnits);
  let assets;

  try {
    const options = {
      filterAssetIds: assetIds.join(),
      titleSortDirection: "ASC",
      fields: ["id", "latitude", "longitude"],
      itemsPerPage: 50,
    };
    assets = (await getAssets({ options })).assets;
  } catch (error) {
    console.error("Error fetching assets:", error);
  }

  return {
    places: assets.map((asset) => [
      asset.longitude,
      asset.latitude,
      formattedRadius,
      asset.id,
      "km",
    ]),
    placesDistUnits: "km",
    boundsType: "assets",
  };
}

function _unformatAssets(assets) {
  return {
    selectedAssets: assets.map((a) => a[3]),
    radius: assets[0][2],
  };
}

function setSourceMetadataDynamicConstants() {
  SOURCE_METADATA.forEach((type) => {
    if (
      isFeatureUnlocked(
        `livesearch-source-metadata-${lodashKebabCase(type)}`
      ) &&
      //Platform is covered by source
      type !== "platform"
    ) {
      ACCEPTED_PARAMS.push(`sourceMetadata${lodashUpperFirst(type)}`);
    }
  });
}
